'use client';

import { useAppDispatch } from '@/hooks/redux';
import { hideModal } from '@/store/slices/modalSlice';
import {
  Modal as NextUIModal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  ModalProps,
} from "@heroui/modal";
import clsx from 'clsx';
import React from 'react';

interface CustomModalProps
  extends Omit<ModalProps, 'children' | 'title' | 'content'> {
  children?: React.ReactNode;
  title?: string | React.ReactNode;
  content?: string | React.ReactNode;
  footer?: string | React.ReactNode;
  name?: string;
  classNames?: {
    base?: string;
    title?: string;
    content?: string;
    footer?: string;
    body?: string;
    backdrop?: string;
    wrapper?: string;
    closeButton?: string;
  };
}

const Modal = React.forwardRef(
  (
    {
      footer,
      content,
      title,
      classNames,
      className,
      ...props
    }: CustomModalProps,
    ref
  ) => {
    const dispatch = useAppDispatch();

    const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure({
      defaultOpen: props.defaultOpen,
    });

    // open outside functions
    React.useImperativeHandle(ref, () => ({
      onOpen,
      onClose,
      onOpenChange,
    }));

    // close modal
    const handleOpenChange = (open: boolean) => {
      onOpenChange();
      if (!open) {
        // modal state will be updated to close
        dispatch(hideModal());
      }
    };

    return (
      <NextUIModal
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        className={clsx(className, classNames?.base)}
        {...props}
        classNames={{
          wrapper: clsx('items-center justify-center', classNames?.wrapper),
          backdrop: classNames?.backdrop,
          closeButton: classNames?.closeButton,
        }}
      >
        {props.children ? (
          <ModalContent className={clsx('p-4', classNames?.content)}>
            {props.children}
          </ModalContent>
        ) : (
          <ModalContent className={classNames?.content}>
            {React.isValidElement(title) ? (
              title
            ) : (
              <ModalHeader className={classNames?.title}>{title}</ModalHeader>
            )}
            {React.isValidElement(content) ? (
              content
            ) : (
              <ModalBody className={classNames?.body}>{content}</ModalBody>
            )}
            {React.isValidElement(footer) ? (
              footer
            ) : (
              <ModalFooter className={classNames?.footer}>{footer}</ModalFooter>
            )}
          </ModalContent>
        )}
      </NextUIModal>
    );
  }
);

export default Modal;
